import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import announcementService from "@/services/announcement.service";

// Schéma de validation simplifié
const simpleAnnouncementSchema = z.object({
  titre: z.string().min(5, "Le titre doit contenir au moins 5 caractères"),
  contenu: z
    .string()
    .min(10, "Le contenu doit contenir au moins 10 caractères"),
  type_annonce: z.string().default("info"),
  priorite: z.string().default("normale"),
  visible_par: z.string().default("tous"),
  actif: z.boolean().default(true),
});

const WorkingAnnouncementModal = ({
  isOpen,
  onClose,
  onSuccess,
  mode = "create",
  announcement = null,
}) => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [typeAnnonce, setTypeAnnonce] = useState("info");
  const [priorite, setPriorite] = useState("normale");
  const [visiblePar, setVisiblePar] = useState("tous");

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm({
    resolver: zodResolver(simpleAnnouncementSchema),
    defaultValues: {
      titre: "",
      contenu: "",
      type_annonce: "info",
      priorite: "normale",
      visible_par: "tous",
      actif: true,
    },
  });

  // Effect to populate form when editing
  useEffect(() => {
    if (mode === "edit" && announcement) {
      // Set form values
      setValue("titre", announcement.titre || "");
      setValue("contenu", announcement.contenu || "");
      setValue("type_annonce", announcement.type_annonce || "info");
      setValue("priorite", announcement.priorite || "normale");
      setValue("visible_par", announcement.visible_par || "tous");
      setValue("actif", announcement.actif ?? true);

      // Set local state
      setTypeAnnonce(announcement.type_annonce || "info");
      setPriorite(announcement.priorite || "normale");
      setVisiblePar(announcement.visible_par || "tous");
    } else if (mode === "create") {
      // Reset to defaults for create mode
      reset({
        titre: "",
        contenu: "",
        type_annonce: "info",
        priorite: "normale",
        visible_par: "tous",
        actif: true,
      });
      setTypeAnnonce("info");
      setPriorite("normale");
      setVisiblePar("tous");
    }
  }, [mode, announcement, setValue, reset]);

  console.log("🔍 [DEBUG] WorkingAnnouncementModal - Form errors:", errors);

  // Synchroniser les states avec react-hook-form
  const handleTypeChange = (value) => {
    setTypeAnnonce(value);
    setValue("type_annonce", value);
  };

  const handlePrioriteChange = (value) => {
    setPriorite(value);
    setValue("priorite", value);
  };

  const handleVisibiliteChange = (value) => {
    setVisiblePar(value);
    setValue("visible_par", value);
  };

  console.log("🔍 [DEBUG] WorkingAnnouncementModal - Form errors:", errors);

  const onSubmit = async (data) => {
    try {
      setIsSubmitting(true);

      let response;
      if (mode === "edit" && announcement?.id) {
        response = await announcementService.updateAnnouncement(
          announcement.id,
          data
        );
        toast({
          title: "Succès",
          description: "Annonce mise à jour avec succès",
        });
      } else {
        response = await announcementService.createAnnouncement(data);
        toast({
          title: "Succès",
          description: "Annonce créée avec succès",
        });
      }

      reset();
      onSuccess && onSuccess();
    } catch (error) {
      console.error("Erreur lors de l'opération:", error);

      toast({
        title: "Erreur",
        description:
          error.message ||
          `Erreur lors de ${
            mode === "edit" ? "la mise à jour" : "la création"
          } de l'annonce`,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>
            {mode === "create" ? "Nouvelle annonce" : "Modifier l'annonce"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Titre */}
          <div>
            <Label htmlFor="titre">Titre *</Label>
            <Input
              id="titre"
              {...register("titre")}
              placeholder="Titre de l'annonce"
              className={errors.titre ? "border-red-500" : ""}
            />
            {errors.titre && (
              <p className="text-sm text-red-500 mt-1">
                {errors.titre.message}
              </p>
            )}
          </div>

          {/* Contenu */}
          <div>
            <Label htmlFor="contenu">Contenu *</Label>
            <Textarea
              id="contenu"
              {...register("contenu")}
              placeholder="Contenu de l'annonce"
              rows={4}
              className={errors.contenu ? "border-red-500" : ""}
            />
            {errors.contenu && (
              <p className="text-sm text-red-500 mt-1">
                {errors.contenu.message}
              </p>
            )}
          </div>

          {/* Type d'annonce */}
          <div>
            <Label htmlFor="type_annonce">Type d'annonce</Label>
            <Select value={typeAnnonce} onValueChange={handleTypeChange}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Sélectionner un type" />
              </SelectTrigger>
              <SelectContent
                className="z-[99999]"
                position="popper"
                sideOffset={4}
              >
                <SelectItem value="info">📢 Information</SelectItem>
                <SelectItem value="urgente">🚨 Urgente</SelectItem>
                <SelectItem value="evenement">📅 Événement</SelectItem>
                <SelectItem value="procedure">📋 Procédure</SelectItem>
                <SelectItem value="celebration">🎉 Célébration</SelectItem>
                <SelectItem value="formation">🎓 Formation</SelectItem>
                <SelectItem value="autre">📌 Autre</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Priorité */}
          <div>
            <Label htmlFor="priorite">Priorité</Label>
            <Select value={priorite} onValueChange={handlePrioriteChange}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Sélectionner une priorité" />
              </SelectTrigger>
              <SelectContent
                className="z-[99999]"
                position="popper"
                sideOffset={4}
              >
                <SelectItem value="normale">Normale</SelectItem>
                <SelectItem value="importante">Importante</SelectItem>
                <SelectItem value="urgente">Urgente</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Visibilité */}
          <div>
            <Label htmlFor="visible_par">Visible par</Label>
            <Select value={visiblePar} onValueChange={handleVisibiliteChange}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Sélectionner la visibilité" />
              </SelectTrigger>
              <SelectContent
                className="z-[99999]"
                position="popper"
                sideOffset={4}
              >
                <SelectItem value="tous">Tous les employés</SelectItem>
                <SelectItem value="departement">Mon département</SelectItem>
                <SelectItem value="equipe">Mon équipe</SelectItem>
                <SelectItem value="role_specifique">Rôle spécifique</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Actif (caché pour simplicité) */}
          <input type="hidden" {...register("actif")} value={true} />

          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Annuler
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting
                ? mode === "edit"
                  ? "Mise à jour..."
                  : "Création..."
                : mode === "edit"
                ? "Mettre à jour"
                : "Créer l'annonce"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default WorkingAnnouncementModal;
